import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ExperienceCard from '../Cards/ExperienceCard';
import { experiences } from '../../data/constants';

gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  padding: 100px 5vw;
  background: ${({ theme }) => theme.bg};
`;

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Label = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.02em;
`;

const Timeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;

  &::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 20px;
    bottom: 20px;
    width: 1px;
    background: ${({ theme }) => theme.border};

    @media (max-width: 640px) {
      left: 15px;
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 28px;
  padding-bottom: 40px;

  @media (max-width: 640px) {
    gap: 20px;
  }
`;

const DotWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 18px;
`;

const Dot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_light};
  flex-shrink: 0;
  margin-left: 11px;

  @media (max-width: 640px) {
    margin-left: 7px;
  }
`;

const Experience = () => {
  const headerRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(headerRef.current,
      { y: 40, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: headerRef.current, start: 'top 85%' }
      }
    );

    const items = timelineRef.current?.querySelectorAll(':scope > *');
    if (items?.length) {
      gsap.fromTo(items,
        { x: -30, opacity: 0 },
        {
          x: 0, opacity: 1, duration: 0.6, ease: 'power3.out',
          stagger: 0.15,
          scrollTrigger: { trigger: timelineRef.current, start: 'top 82%' }
        }
      );
    }
  }, []);

  return (
    <Section id="experience">
      <Wrapper>
        <Header ref={headerRef}>
          <Label>Career</Label>
          <Title>Experience</Title>
        </Header>
        <Timeline ref={timelineRef}>
          {experiences.map((experience, index) => (
            <TimelineItem key={index}>
              <DotWrapper>
                <Dot />
              </DotWrapper>
              <ExperienceCard experience={experience} />
            </TimelineItem>
          ))}
        </Timeline>
      </Wrapper>
    </Section>
  );
};

export default Experience;
